import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import BulkAddCardsModal from '../components/deckBuilder/BulkAddCardsModal';
import PreviewModal from '../components/deckBuilder/PreviewModal';
import './DeckBuilderPage.css';

const DeckBuilderPage = () => {
  // State management
  const [deckName, setDeckName] = useState('');
  const [deckDescription, setDeckDescription] = useState('');
  const [blackCards, setBlackCards] = useState([]);
  const [whiteCards, setWhiteCards] = useState([]);
  const [showAddBlackModal, setShowAddBlackModal] = useState(false);
  const [showAddWhiteModal, setShowAddWhiteModal] = useState(false);
  const [showPreviewModal, setShowPreviewModal] = useState(false);

  // Validation logic
  const isValid = useMemo(() => {
    return deckName.trim().length > 0 && blackCards.length >= 10 && whiteCards.length >= 50;
  }, [deckName, blackCards, whiteCards]);

  // Add black cards
  const handleAddBlackCards = (cards) => {
    setBlackCards([...blackCards, ...cards]);
    setShowAddBlackModal(false);
  };

  // Add white cards
  const handleAddWhiteCards = (cards) => {
    setWhiteCards([...whiteCards, ...cards]);
    setShowAddWhiteModal(false);
  };

  // Delete card
  const handleDeleteBlackCard = (index) => {
    setBlackCards(blackCards.filter((_, i) => i !== index));
  };

  const handleDeleteWhiteCard = (index) => {
    setWhiteCards(whiteCards.filter((_, i) => i !== index));
  };

  // Download JSON
  const handleDownload = () => {
    const deckData = {
      name: deckName,
      description: deckDescription,
      blackCards: blackCards.map((c) => ({
        text: c.text,
        blanks: c.blanks,
      })),
      whiteCards: whiteCards.map((c) => c.text),
    };

    const blob = new Blob([JSON.stringify(deckData, null, 2)], {
      type: 'application/json',
    });

    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${deckName.replace(/[^a-z0-9]/gi, '-').toLowerCase()}.json`;
    link.click();
    URL.revokeObjectURL(url);
  };

  // Start fresh
  const handleStartFresh = () => {
    if (blackCards.length > 0 || whiteCards.length > 0 || deckName || deckDescription) {
      const confirmed = window.confirm(
        `This will delete:\n• ${blackCards.length} black cards\n• ${whiteCards.length} white cards\n• Deck name and description\n\nThis cannot be undone.`
      );
      if (!confirmed) return;
    }

    setDeckName('');
    setDeckDescription('');
    setBlackCards([]);
    setWhiteCards([]);
  };

  // Quick Start Template
  const handleQuickStart = () => {
    if (blackCards.length > 0 || whiteCards.length > 0 || deckName || deckDescription) {
      const confirmed = window.confirm(
        'This will replace your current deck with a template. Continue?'
      );
      if (!confirmed) return;
    }

    // Template question cards (10 cards)
    const templateBlackCards = [
      { text: 'What is the best thing about ___?', blanks: 1 },
      { text: 'Why did ___ happen?', blanks: 1 },
      { text: 'The secret to success is ___.', blanks: 1 },
      { text: '___ is the best way to start your day.', blanks: 1 },
      { text: 'I would pay good money to see ___.', blanks: 1 },
      { text: 'What is your favorite ___?', blanks: 1 },
      { text: 'Life would be better with more ___.', blanks: 1 },
      { text: '___ makes everything better.', blanks: 1 },
      { text: 'The worst thing you could find in ___ is ___.', blanks: 2 },
      { text: 'If I could combine ___ with ___, life would be perfect.', blanks: 2 },
    ];

    // Template answer cards (50 cards)
    const templateWhiteCards = [
      { text: 'A good book' },
      { text: 'Coffee in the morning' },
      { text: 'A warm blanket' },
      { text: 'Your favorite song' },
      { text: 'A day at the beach' },
      { text: 'Pizza' },
      { text: 'Chocolate' },
      { text: 'A cozy fireplace' },
      { text: 'Fresh flowers' },
      { text: 'A perfect sunset' },
      { text: 'Stargazing' },
      { text: 'Dancing in the rain' },
      { text: 'A surprise gift' },
      { text: 'Laughter with friends' },
      { text: 'A new adventure' },
      { text: 'Homemade cookies' },
      { text: 'A bubble bath' },
      { text: 'Your favorite movie' },
      { text: 'A comfortable chair' },
      { text: 'Ice cream' },
      { text: 'A good nap' },
      { text: 'Puppies' },
      { text: 'Kittens' },
      { text: 'A rainbow' },
      { text: 'Birthday cake' },
      { text: 'Breakfast in bed' },
      { text: 'A road trip' },
      { text: 'Your best friend' },
      { text: 'A family gathering' },
      { text: 'The weekend' },
      { text: 'Summer vacation' },
      { text: 'Snow day' },
      { text: 'A campfire' },
      { text: 'The ocean' },
      { text: 'Mountains' },
      { text: 'A city skyline' },
      { text: 'Live music' },
      { text: 'A carnival' },
      { text: 'Fireworks' },
      { text: 'Game night' },
      { text: 'Movie marathon' },
      { text: 'Brunch' },
      { text: 'Tacos' },
      { text: 'Your phone' },
      { text: 'Wi-Fi' },
      { text: 'Air conditioning' },
      { text: 'A good joke' },
      { text: 'Freedom' },
      { text: 'Creativity' },
      { text: 'Love' },
    ];

    setDeckName('My Template Deck');
    setDeckDescription('A starter template deck - edit and customize as you like!');
    setBlackCards(templateBlackCards);
    setWhiteCards(templateWhiteCards);
  };

  return (
    <div className="deck-builder-page">
      <div className="deck-builder-container">
        {/* Header */}
        <div className="deck-builder-header">
          <h1 className="deck-builder-title">😏 Deck Around and Find Out</h1>
          <Link to="/" className="btn-home">
            Home
          </Link>
        </div>

        {/* Deck Metadata - Placeholder for now */}
        <div className="deck-metadata">
          <div className="form-group">
            <label htmlFor="deck-name">Deck Name *</label>
            <input
              type="text"
              id="deck-name"
              className="input-field"
              placeholder="My Big Fat Deck"
              value={deckName}
              onChange={(e) => setDeckName(e.target.value)}
            />
          </div>
          <div className="form-group">
            <label htmlFor="deck-description">Description (optional)</label>
            <input
              type="text"
              id="deck-description"
              className="input-field"
              placeholder="What's this deck about?"
              value={deckDescription}
              onChange={(e) => setDeckDescription(e.target.value)}
            />
          </div>
        </div>

        {/* Black Cards Section - Placeholder */}
        <div className="cards-section black-cards-section">
          <div className="section-header">
            <h2>Question Cards</h2>
            <div className="card-counter">
              {blackCards.length >= 10 ? '✅' : '❌'} {blackCards.length} cards (min 10 required)
            </div>
          </div>
          <div className="cards-list">
            {blackCards.map((card, index) => (
              <div key={index} className="card-item">
                <div className="card-content">
                  <span className="card-text">{card.text}</span>
                  <span className="card-blanks">[{card.blanks} blank(s)]</span>
                </div>
                <div className="card-actions">
                  <button
                    className="btn-icon"
                    onClick={() => handleDeleteBlackCard(index)}
                    aria-label="Delete card"
                  >
                    🗑️
                  </button>
                </div>
              </div>
            ))}
          </div>
          <button className="btn btn-add" onClick={() => setShowAddBlackModal(true)}>
            + Add Cards
          </button>
        </div>

        {/* White Cards Section - Placeholder */}
        <div className="cards-section white-cards-section">
          <div className="section-header">
            <h2>Answer Cards</h2>
            <div className="card-counter">
              {whiteCards.length >= 50 ? '✅' : '❌'} {whiteCards.length} cards (min 50 required)
            </div>
          </div>
          <div className="cards-list">
            {whiteCards.slice(0, 10).map((card, index) => (
              <div key={index} className="card-item">
                <div className="card-content">
                  <span className="card-text">{card.text}</span>
                </div>
                <div className="card-actions">
                  <button
                    className="btn-icon"
                    onClick={() => handleDeleteWhiteCard(index)}
                    aria-label="Delete card"
                  >
                    🗑️
                  </button>
                </div>
              </div>
            ))}
            {whiteCards.length > 10 && (
              <div className="card-item more-indicator">
                ... and {whiteCards.length - 10} more cards
              </div>
            )}
          </div>
          <button className="btn btn-add" onClick={() => setShowAddWhiteModal(true)}>
            + Add Cards
          </button>
        </div>

        {/* Action Bar */}
        <div className="action-bar">
          <div className="action-left">
            <button className="btn btn-template" onClick={handleQuickStart}>
              ⚡ Quick Start Template
            </button>
            <button className="btn btn-secondary" onClick={handleStartFresh}>
              Start Fresh
            </button>
          </div>
          <div className="action-right">
            <button
              className="btn btn-preview"
              onClick={() => setShowPreviewModal(true)}
              disabled={blackCards.length === 0 && whiteCards.length === 0}
              title={
                blackCards.length === 0 && whiteCards.length === 0
                  ? 'Add cards to preview them'
                  : 'Preview how your cards will look in-game'
              }
            >
              👁️ Preview
            </button>
            <button
              className="btn btn-primary"
              onClick={handleDownload}
              disabled={!isValid}
              title={
                !isValid
                  ? `Need: ${deckName ? '' : 'deck name, '}${blackCards.length < 10 ? `${10 - blackCards.length} more black cards, ` : ''}${whiteCards.length < 50 ? `${50 - whiteCards.length} more white cards` : ''}`
                  : 'Download deck as JSON'
              }
            >
              Download JSON
            </button>
          </div>
        </div>

        {/* Modals */}
        {showAddBlackModal && (
          <BulkAddCardsModal
            type="black"
            onAdd={handleAddBlackCards}
            onClose={() => setShowAddBlackModal(false)}
          />
        )}

        {showAddWhiteModal && (
          <BulkAddCardsModal
            type="white"
            onAdd={handleAddWhiteCards}
            onClose={() => setShowAddWhiteModal(false)}
          />
        )}

        {showPreviewModal && (
          <PreviewModal
            blackCards={blackCards}
            whiteCards={whiteCards}
            onClose={() => setShowPreviewModal(false)}
          />
        )}
      </div>
    </div>
  );
};

export default DeckBuilderPage;
